import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GTuple } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/label';

import { IItemCount } from '@educraft/base';

/**
 * Draw a Mobilka (matching) board into `baseRect` on `page`.
 */
function drawMobilka(
  page: PageGen,
  opts: OptsLabel,
  countX: number,
  countY: number,
  items: GTuple[],
  baseRect: PdfRect,
  margin: number
): void {
  if (items.length === 0) return;

  const count = Math.min(items.length, Math.floor((countX * countY) / 2)); // questions shown in top half

  // Fisher-Yates shuffle map for answer circles
  const map = Array.from({ length: items.length }, (_, i) => i);
  const rnd = opts.data.random;
  for (let k = map.length - 1; k > 0; k--) {
    const j = rnd.next(0, k + 1);
    [map[k], map[j]] = [map[j], map[k]];
  }

  const topRect = new PdfRect(baseRect.x, baseRect.y, baseRect.width, baseRect.height / 2);
  const bottomRect = new PdfRect(
    baseRect.x,
    baseRect.y + baseRect.height / 2,
    baseRect.width,
    baseRect.height / 2
  );

  // Circle size that fits countX × countY in bottom half
  const size = Math.min(
    (bottomRect.width - margin * (countX + 1)) / countX,
    (bottomRect.height - margin * (countY + 1)) / countY
  );

  // Centre the circle grid inside bottomRect
  const originX = bottomRect.x + (bottomRect.width - countX * size - (countX - 1) * margin) / 2;
  const originY = bottomRect.bottom - countY * (size + margin);
  const topLine = originY - margin; // stub line Y

  const qH = (topRect.height - margin * (count - 1)) / count;

  // Measure fonts
  const questionTexts = items.slice(0, count).map((i) => [i.left.getText()]);
  const answerTexts = items.map((i) => [i.right.getText()]);

  const fontQuestion = page.adjustFontMulti(
    questionTexts,
    GUtil.baseFont,
    new PdfSize(topRect.width - 2 * margin, qH)
  );
  const fontAnswer = page.adjustFontMulti(
    answerTexts,
    GUtil.baseFont,
    new PdfSize(size * 0.9, size * 0.9)
  );

  // Top half: question rows (first `count` items)
  for (let i = 0; i < count; i++) {
    const qY = topRect.y + margin + i * qH;
    const rect = new PdfRect(topRect.x + margin, qY, topRect.width - 2 * margin, qH);
    page.drawLabel(items[i].left, { rect, font: fontQuestion });
  }

  // Bottom half: shuffled answer circles (all items)
  for (let i = 0; i < items.length; i++) {
    const index = map[i];
    const x = i % countX;
    const y = Math.floor(i / countX);
    const rect = new PdfRect(
      originX + x * (size + margin),
      originY + y * (size + margin),
      size,
      size
    );
    page.drawEllipse(GUtil.basePen, null, rect);
    page.drawLabel(items[index].right, { rect, font: fontAnswer });
  }

  // Junction stubs: one short horizontal line per question column
  for (let i = 0; i < count; i++) {
    const x1 = baseRect.x + margin + i * (margin + size);
    page.drawLine(GUtil.basePen, new PdfPoint(x1, topLine), new PdfPoint(x1 + size, topLine));
  }
}

/**
 * Mobilka__Base — 4-column × 2-row matching board, portrait A4.
 */
export class Mobilka__Base implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 8;
  }
  countMax(): number {
    return 8;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const itemsPerPage = 8;
    for (let i = 0; i < opts.list.length; i += itemsPerPage) {
      const page = pdf.addPage();
      const currentItems = opts.list.slice(i, i + itemsPerPage);
      drawMobilka(
        page,
        opts,
        4,
        2,
        currentItems,
        new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height),
        PdfUnit.FromMillimeters(10)
      );
    }
  }
}

/**
 * Mobilka__Mini — two 5×2 matching boards side-by-side, landscape A4.
 */
export class Mobilka__Mini implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 20;
  }
  countMin(): number {
    return 20;
  }
  countMax(): number {
    return 20;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const itemsPerPage = 20;
    const margin = PdfUnit.FromMillimeters(15);
    const round = PdfUnit.FromMillimeters(20);

    for (let i = 0; i < opts.list.length; i += itemsPerPage) {
      const page = pdf.addPage(true); // landscape
      const currentItems = opts.list.slice(i, i + itemsPerPage);

      const halfW = PdfSize.A4H.width / 2;
      const r1 = new PdfRect(0, 0, halfW, PdfSize.A4H.height).deflate(margin);
      const r2 = new PdfRect(halfW, 0, halfW, PdfSize.A4H.height).deflate(margin);

      page.drawRoundedRectangle(GUtil.basePen, null, r1, round);
      page.drawRoundedRectangle(GUtil.basePen, null, r2, round);

      drawMobilka(page, opts, 5, 2, currentItems.slice(0, 10), r1, PdfUnit.FromMillimeters(5));
      drawMobilka(page, opts, 5, 2, currentItems.slice(10, 20), r2, PdfUnit.FromMillimeters(5));
    }
  }
}
